import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Badge } from "@/components/Common";
import { fmtDateTime } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Loader2, KeyRound, Wand2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "admin", label: "Amministratore di Sistema" },
  { value: "amministrazione", label: "Amministrazione" },
  { value: "didattica", label: "Didattica / Erogazione" },
  { value: "direzione", label: "Direzione" },
  { value: "azienda", label: "Azienda (esterno)" },
  { value: "docente", label: "Docente (esterno)" },
  { value: "dipendente", label: "Dipendente (esterno)" },
];

export default function Amministrazione() {
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "didattica", password: "" });

  const loadUsers = () => api.get("/users").then((r) => setUsers(r.data));
  const loadLogs = () => api.get("/audit/log").then((r) => setLogs(r.data));

  useEffect(() => { loadUsers(); loadLogs(); }, []);

  const createUser = async () => {
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Utente creato");
      setOpen(false);
      setForm({ email: "", name: "", role: "didattica", password: "" });
      loadUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const [resetUser, setResetUser] = useState(null);
  const [pwd, setPwd] = useState({ p1: "", p2: "" });

  const genPwd = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
    const arr = new Uint32Array(12);
    window.crypto.getRandomValues(arr);
    const p = Array.from(arr, (n) => chars[n % chars.length]).join("");
    setPwd({ p1: p, p2: p });
  };

  const doReset = async () => {
    if (pwd.p1.length < 8) { toast.error("La password deve contenere almeno 8 caratteri"); return; }
    if (pwd.p1 !== pwd.p2) { toast.error("Le due password non coincidono"); return; }
    setSaving(true);
    try {
      await api.post(`/users/${resetUser.id}/reset-password`, { password: pwd.p1 });
      toast.success(`Password reimpostata per ${resetUser.email}`);
      setResetUser(null);
      setPwd({ p1: "", p2: "" });
      loadLogs();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    await api.put(`/users/${u.id}`, { active: !u.active });
    toast.success("Utente aggiornato");
    loadUsers();
  };

  return (
    <div data-testid="amministrazione-page">
      <PageHeader title="Amministrazione" subtitle="Utenti, ruoli e log di audit" />

      <Tabs defaultValue="utenti">
        <TabsList>
          <TabsTrigger value="utenti" data-testid="tab-utenti">Utenti & Ruoli</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-log">Log di Audit</TabsTrigger>
        </TabsList>

        <TabsContent value="utenti" className="mt-4">
          <div className="mb-4 flex justify-end">
            <Button onClick={() => setOpen(true)} data-testid="new-user-btn" className="bg-[#E11D48] hover:bg-[#BE123C]">
              <Plus className="mr-1.5 h-4 w-4" /> Nuovo Utente
            </Button>
          </div>
          <div className="rounded-xl border bg-white overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs uppercase text-slate-500">Nome</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Email</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Ruolo</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Ultimo accesso</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Stato</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid="user-row">
                    <TableCell className="font-medium text-slate-900">{u.name}</TableCell>
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell><Badge label={u.role_label} cls="bg-slate-100 text-slate-600 border-slate-200" /></TableCell>
                    <TableCell className="text-sm">{u.last_login ? fmtDateTime(u.last_login) : "—"}</TableCell>
                    <TableCell>
                      <button onClick={() => toggleActive(u)} data-testid="toggle-user">
                        <Badge label={u.active ? "Attivo" : "Disattivo"} cls={u.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"} />
                      </button>
                    </TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm" onClick={() => { setResetUser(u); setPwd({ p1: "", p2: "" }); }} data-testid="reset-password-btn">
                        <KeyRound className="mr-1.5 h-4 w-4" /> Reimposta password
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="log" className="mt-4">
          <div className="rounded-xl border bg-white overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs uppercase text-slate-500">Data/Ora</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Utente</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Azione</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Entità</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Dettaglio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((l) => (
                  <TableRow key={l.id} data-testid="log-row">
                    <TableCell className="font-mono text-xs">{fmtDateTime(l.timestamp)}</TableCell>
                    <TableCell className="text-sm">{l.user_email}</TableCell>
                    <TableCell><Badge label={l.action} cls="bg-slate-100 text-slate-600 border-slate-200" /></TableCell>
                    <TableCell className="text-sm">{l.entity}</TableCell>
                    <TableCell className="text-sm text-slate-500">{l.detail}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle>Reimposta password</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Nuova password per <span className="font-medium text-slate-800">{resetUser?.email}</span>. Comunicala all'utente in modo sicuro: non sarà più visibile dopo il salvataggio.</p>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Nuova password (min. 8 caratteri)</Label>
              <div className="mt-1 flex gap-2">
                <Input type="text" value={pwd.p1} onChange={(e) => setPwd({ ...pwd, p1: e.target.value })} className="font-mono" data-testid="reset-pwd-1" />
                <Button type="button" variant="outline" onClick={genPwd} title="Genera password" data-testid="reset-pwd-generate"><Wand2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Conferma password</Label>
              <Input type="text" value={pwd.p2} onChange={(e) => setPwd({ ...pwd, p2: e.target.value })} className="mt-1 font-mono" data-testid="reset-pwd-2" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetUser(null)}>Annulla</Button>
            <Button onClick={doReset} disabled={saving} className="bg-[#E11D48] hover:bg-[#BE123C]" data-testid="reset-pwd-save">
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Salva password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo Utente</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="user-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="user-email" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Ruolo</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Password</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Indar2026!" className="mt-1" data-testid="user-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={createUser} disabled={saving} className="bg-[#E11D48] hover:bg-[#BE123C]" data-testid="save-user-btn">
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Crea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
